/* @flow @format */

import type { Reducer } from 'redux';
import { createSelector } from 'reselect';
import { denormalize } from 'normalizr';
import F from 'lodash/fp';
import * as L from 'partial.lenses';

import { type ActionType, createAction } from '../utils';
import type { FailurePayload } from '../types';
import { courseSchema } from '../schema';

type CommonProps = {|
  +uid: string,
  fetchedAt: number, // timestamp
|};

export type Contest = {
  ...CommonProps,
  +title: string,
  +tagList: string[],
  +courses: string[], // array of uids
};

export type Course = {
  ...CommonProps,
  +title: string,
  +tagList: string[],
  +courseContents: string[], // array of uids
};

export type CourseContent = {
  ...CommonProps,
  +s3Url: string,
  +tagList: string[],
};

type Repo<T> = {
  +byId: { [string]: T },
  +meta: {
    +loading: boolean,
  },
};

export type Contests = Repo<Contest>;
export type Courses = Repo<Course>;
export type CourseContents = Repo<CourseContent>;
export type Qdls = Repo<string>;

export type Entities = {
  +contests: Contests,
  +courses: Courses,
  +courseContents: CourseContents,
  +qdls: Qdls,
};

// Actions

export const FETCH_ENTITIES_BEGIN: 'proc/entitites/FETCH_ENTITIES_BEGIN' = 'proc/entitites/FETCH_ENTITIES_BEGIN';
export const FETCH_ENTITIES_SUCCESS: 'proc/entitites/FETCH_ENTITIES_SUCCESS' = 'proc/entitites/FETCH_ENTITIES_SUCCESS';
export const FETCH_ENTITIES_FAILURE: 'proc/entitites/FETCH_ENTITIES_FAILURE' = 'proc/entitites/FETCH_ENTITIES_FAILURE';
export const CLEAR_ALL_ENTITIES: 'proc/entities/CLEAR_ALL_ENTITIES' = 'proc/entities/CLEAR_ALL_ENTITIES';

export type FetchEntitiesPayload = { targetType: 'contests' } | { targetType: 'contest', uid: string };

type FetchTarget = $PropertyType<FetchEntitiesPayload, 'targetType'>;

type FetchEntitiesResultPayload = { targetType: FetchTarget, entities: any };

export const fetchEntitiesBegin = createAction<typeof FETCH_ENTITIES_BEGIN, FetchEntitiesPayload>(FETCH_ENTITIES_BEGIN);
export function fetchAllContestsBegin() {
  return fetchEntitiesBegin({ targetType: 'contests' });
}
export function fetchContestBegin(uid: string) {
  return fetchEntitiesBegin({ targetType: 'contest', uid });
}
export const fetchEntitiesSuccess = createAction<typeof FETCH_ENTITIES_SUCCESS, FetchEntitiesResultPayload>(
  FETCH_ENTITIES_SUCCESS
);
export const fetchEntitiesFailure = createAction<typeof FETCH_ENTITIES_FAILURE, FailurePayload<FetchEntitiesPayload>>(
  FETCH_ENTITIES_FAILURE
);
export const clearAllEntities = createAction<typeof CLEAR_ALL_ENTITIES, void>(CLEAR_ALL_ENTITIES);

export type EntitiesActionType =
  | ActionType<typeof fetchEntitiesBegin>
  | ActionType<typeof fetchEntitiesSuccess>
  | ActionType<typeof fetchEntitiesFailure>
  | ActionType<typeof clearAllEntities>;

// Reducer

const emptyRepo = { byId: {}, meta: { loading: false } };
const initialEntities: Entities = {
  contests: emptyRepo,
  courses: emptyRepo,
  courseContents: emptyRepo,
  qdls: emptyRepo,
};

function loadingForTarget(targetType: FetchTarget) {
  switch (targetType) {
    case 'contest':
    case 'contests':
      return ['contests', 'meta', 'loading'];
    default:
      /*:: (targetType: empty) */
      throw new TypeError(`Unknown fetch targetType: ${targetType}`);
  }
}

const reducer: Reducer<Entities, EntitiesActionType> = (state = initialEntities, action) => {
  switch (action.type) {
    case FETCH_ENTITIES_BEGIN:
      return L.set(loadingForTarget(action.payload.targetType), true, state);
    case FETCH_ENTITIES_FAILURE:
      return L.set(loadingForTarget(action.payload.originalPayload.targetType), false, state); // set loading flag to false
    case FETCH_ENTITIES_SUCCESS: {
      const { targetType, entities } = action.payload;
      const repoKeys: string[] = Object.keys(entities);
      return F.pipe(
        state => {
          if (targetType == 'contests') {
            // reset entities when fetch all contests
            return initialEntities;
          } else {
            return L.set(loadingForTarget(targetType), false, state); // set loading flag to false
          }
        },
        state => F.reduce((state, repoKey) => L.assign([repoKey, 'byId'], entities[repoKey], state), state, repoKeys)
      )(state);
    }
    case CLEAR_ALL_ENTITIES:
      return initialEntities;
    default:
      /*:: (action.type: empty) */
      return state;
  }
};
export default reducer;

// Selectors
export const getProps = (_: any, props: any) => props;

type Selector<T> = (state: Entities, ...rest: any[]) => T;

export const getAllContests: Selector<*> = (state: Entities) => state.contests.byId;
export const getContestById: Selector<?Contest> = (state, uid) => getAllContests(state)[uid];

export const getIsContestLoading: Selector<boolean> = state => Boolean(state.contests.meta.loading);
export const getIsContestStale: Selector<boolean> = (state, uid) => {
  // can't use reselect here, because the returned values depends on current time
  const contest = getContestById(state, uid);
  if (!contest || !contest.fetchedAt) return true; // no data yet
  return Date.now() > contest.fetchedAt + 3600000; // cache for 1 hour
};

export const getAllContestTags: Selector<string[]> = createSelector(getAllContests, F.flatMap('tagList'));

export const getHasContestType: Selector<boolean> = createSelector(
  getAllContestTags,
  getProps,
  (tags, { contestType }) => F.includes(contestType, tags)
);

export const getContestByType: Selector<?Contest> = createSelector(
  getAllContests,
  getProps,
  (contests, { contestType }) => F.find(c => c && F.includes(contestType, c.tagList), contests)
);

export const getAllCourses: Selector<*> = (state: Entities) => state.courses.byId;
export const getAllCourseUids: Selector<string[]> = createSelector(getAllCourses, courses => Object.keys(courses));

export const getAllCoursesForContestType: Selector<Array<Course>> = createSelector(
  getAllCourses,
  getAllCourseUids,
  getContestByType,
  (courses, allCourseUids, contest) => {
    if (!contest) return [];
    return F.pipe(
      F.filter(uid => F.includes(uid, contest.courses)),
      F.map(uid => courses[uid])
    )(allCourseUids);
  }
);

export const getAllCoursesForContestTypeByCourseType: Selector<
  Array<Course>
> = createSelector(getAllCoursesForContestType, getProps, (courses, { courseType }) =>
  F.filter(c => F.includes(courseType, c.tagList), courses)
);

export const getAllCourseContents: Selector<*> = state => state.courseContents.byId;
export const getCourseContentById: Selector<?CourseContent> = (state, uid) => getAllCourseContents(state)[uid];

const getCourseUidsInContest = (_, contest: Contest) => contest.courses;
export const getAllCoursesForContest: Selector<Array<Course>> = createSelector(
  getAllCourses,
  getAllCourseContents,
  getCourseUidsInContest,
  (courses, courseContents, uids) => denormalize(uids, [courseSchema], { courses, courseContents })
);

const makeGetAllCoursesForContestByType = (courseType: 'mission' | 'field') =>
  createSelector(
    getAllCoursesForContest,
    F.filter(course => F.includes(courseType, course.tagList))
  );

export const getAllMissionCoursesForContest: Selector<Array<Course>> = makeGetAllCoursesForContestByType('mission');
export const getAllFieldCoursesForContest: Selector<Array<Course>> = makeGetAllCoursesForContestByType('field');
